import { z } from 'zod'

/** API / reset payload may use number; Angular org profile uses numeric zip + max length 6. */
function organizationZipFromMixed() {
  return z
    .union([z.string(), z.number(), z.null(), z.undefined()])
    .transform((v) => (v === undefined || v === null ? '' : String(v).trim()))
}

/** Angular `mobileValidator`: digits-only match after stripping `\D` (non-empty values). */
export function isOrganizationPhoneValid(value: string): boolean {
  const t = value.trim()
  if (!t) return true
  const mobile = t.replace(/\D/g, '')
  return /^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(mobile)
}

export const organizationFormSchema = z.object({
  org_name: z.string().min(1, 'Organization name is required'),
  org_abbrev: z
    .string()
    .min(1, 'Abbreviation is required')
    .max(3, 'Abbreviation must be at most 3 characters'),
  charity: z.union([z.enum(['0', '1']), z.literal('')]).refine((val) => val === '0' || val === '1', {
    message: 'Charity selection is required',
  }),
  org_website: z
    .string()
    .refine(
      (v) => {
        const t = v.trim()
        if (!t) return true
        if (/^(ftp|mailto|javascript):/i.test(t)) return false
        try {
          const withScheme = /^https?:\/\//i.test(t) ? t : `https://${t}`
          const url = new URL(withScheme)
          return url.protocol === 'http:' || url.protocol === 'https:'
        } catch {
          return false
        }
      },
      { message: 'Enter a valid website URL' },
    ),
  /** Angular: `Validators.minLength(10)` + `mobileValidator` on `org_phone` (optional: empty is valid). */
  org_phone: z
    .string()
    .refine(
      (v) => {
        const t = v.trim()
        if (!t) return true
        return t.length >= 10
      },
      { message: 'Phone number length must be 10 Character' },
    )
    .refine(isOrganizationPhoneValid, {
      message: 'Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX]',
    }),
  org_email: z.string().refine(
    (v) => v === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()),
    { message: 'Must be a valid email' },
  ),
  org_address: z.string(),
  org_city: z.string(),
  org_state: z.string(),
  /** Angular: numeric zip, `appMaxLength` 6 on org profile (`type="number"` + directive). */
  org_zipcode: organizationZipFromMixed().pipe(
    z.union([
      z.literal(''),
      z.string().regex(/^\d{1,6}$/, 'Zipcode must be numeric (max 6 digits)'),
    ]),
  ),
  org_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Primary color must be a valid hex color'),
  email_footer: z.string().min(1, 'Email footer is required'),
  system_announcement: z.string(),
  about_content: z.string(),
  about_content_applicant: z.string(),
  about_content_pmc: z.string(),
  stock_letter: z.string(),
  lost_revenue_content: z.string(),
  payment_information_row: z.string(),
  skip_claim_form: z.string(),
  default_payment_statement_form: z.string(),
  certificate_template: z.string().optional(),
})

/** Raw field values (RHF); `org_zipcode` may be string | number | null from API defaults. */
export type OrganizationFormInput = z.input<typeof organizationFormSchema>
/** After Zod parse (trim/coerce); use for submit payload mapping. */
export type OrganizationFormValues = z.output<typeof organizationFormSchema>
